import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { MdAdd, MdEdit, MdDelete, MdPeople, MdLock } from 'react-icons/md';
import { toast } from 'react-toastify';
import { fetchOrganizationRoles, deleteOrganizationRole } from '../../../services/Http/Api';
import SectionTitle from '../../../shared/layout/titles/SectionTitle';
import MainButton from '../../../shared/layout/buttons/MainButton';
import IconButton from '../../../shared/layout/buttons/IconButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import RoleModal from './components/RoleModal';

const RoleBadge = styled.span`
    padding: 0.375rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    background: ${props => props.$isSystem ? 'rgba(130, 22, 146, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
    color: ${props => props.$isSystem ? '#821692' : '#3b82f6'};
    border: 1px solid ${props => props.$isSystem ? 'rgba(130, 22, 146, 0.2)' : 'rgba(59, 130, 246, 0.2)'};
    display: inline-block;
`;

const PermissionBadge = styled.span`
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.6875rem;
    font-weight: 600;
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
    display: inline-block;
    background: ${props => {
        switch (props.$level) {
            case 'delete': return 'rgba(239, 68, 68, 0.1)';
            case 'write': return 'rgba(34, 197, 94, 0.1)';
            case 'read': return 'rgba(59, 130, 246, 0.1)';
            default: return 'rgba(156, 163, 175, 0.1)';
        }
    }};
    color: ${props => {
        switch (props.$level) {
            case 'delete': return '#dc2626';
            case 'write': return '#16a34a';
            case 'read': return '#2563eb';
            default: return '#6b7280';
        }
    }};
    border: 1px solid ${props => {
        switch (props.$level) {
            case 'delete': return 'rgba(239, 68, 68, 0.2)';
            case 'write': return 'rgba(34, 197, 94, 0.2)';
            case 'read': return 'rgba(59, 130, 246, 0.2)';
            default: return 'rgba(156, 163, 175, 0.2)';
        }
    }};
`;

const PermissionsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    max-width: 400px;
`;

const UserCount = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #6b7280;
    font-size: 0.875rem;
`;

const ActionWrapper = styled.div`
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    width: 100%;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    color: #6b7280;
    text-align: center;

    svg {
        font-size: 3rem;
        margin-bottom: 1rem;
        opacity: 0.5;
    }

    h3 {
        margin: 0 0 0.5rem 0;
        color: #374151;
    }

    p {
        margin: 0;
        font-size: 0.875rem;
    }
`;

const RolesManagement = () => {
    const { t } = useTranslation();
    const [roles, setRoles] = useState([]);
    const [options, setOptions] = useState({ modules: [], permission_levels: [] });
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedRole, setSelectedRole] = useState(null);

    const loadRoles = async () => {
        setLoading(true);
        try {
            const response = await fetchOrganizationRoles();
            setRoles(response.data || []);
            setOptions(response.options || { modules: [], permission_levels: [] });
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error(t('Users.Roles.loadError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRoles();
    }, []);

    const handleAddRole = () => {
        setModalMode('create');
        setSelectedRole(null);
        setShowModal(true);
    };

    const handleEditRole = (role) => {
        setModalMode('edit');
        setSelectedRole(role);
        setShowModal(true);
    };

    const handleDeleteRole = async (role) => {
        if (role.is_system_default) {
            toast.error(t('Users.Roles.cannotDeleteSystem'));
            return;
        }

        if (role.users_count > 0) {
            toast.error(t('Users.Roles.cannotDeleteWithUsers'));
            return;
        }

        if (!window.confirm(t('Users.Roles.deleteConfirm'))) {
            return;
        }

        try {
            await deleteOrganizationRole(role.id);
            toast.success(t('Users.Roles.deleteSuccess'));
            loadRoles();
        } catch (error) {
            console.error('Error deleting role:', error);
            toast.error(t('Users.Roles.deleteError'));
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedRole(null);
        setModalMode('create');
    };

    const handleRoleSaved = () => {
        loadRoles();
        handleModalClose();
    };

    const getPermissionSummary = (permissions) => {
        if (!permissions) return [];

        // Only show non-none permissions
        return Object.entries(permissions)
            .filter(([_, level]) => level !== 'none')
            .slice(0, 6) // Show first 6
            .map(([module, level]) => ({ module, level }));
    };

    const columns = [
        {
            name: t('Users.Roles.columns.name'),
            selector: row => row.display_name,
            sortable: true,
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <MdLock size={20} color="#821692" />
                    <div>
                        <div style={{ fontWeight: 600, color: '#111827' }}>{row.display_name}</div>
                        {row.description && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.125rem' }}>
                                {row.description}
                            </div>
                        )}
                    </div>
                </div>
            ),
            grow: 2,
        },
        {
            name: t('Users.Roles.columns.type'),
            selector: row => row.is_system_default,
            sortable: true,
            cell: row => (
                <RoleBadge $isSystem={row.is_system_default}>
                    {row.is_system_default
                        ? t('Users.Roles.systemRole')
                        : t('Users.Roles.customRole')}
                </RoleBadge>
            ),
            width: '7.5rem',
        },
        {
            name: t('Users.Roles.columns.permissions'),
            cell: row => {
                const permissions = getPermissionSummary(row.permissions);
                const totalPermissions = Object.entries(row.permissions || {})
                    .filter(([_, level]) => level !== 'none').length;

                return (
                    <PermissionsContainer>
                        {permissions.map(({ module, level }) => (
                            <PermissionBadge key={module} $level={level}>
                                {module}: {level}
                            </PermissionBadge>
                        ))}
                        {totalPermissions > 6 && (
                            <PermissionBadge $level="none">
                                +{totalPermissions - 6} more
                            </PermissionBadge>
                        )}
                    </PermissionsContainer>
                );
            },
            grow: 3,
        },
        {
            name: t('Users.Roles.columns.users'),
            selector: row => row.users_count,
            sortable: true,
            cell: row => (
                <UserCount>
                    <MdPeople size={18} />
                    <span>{row.users_count || 0}</span>
                </UserCount>
            ),
            width: '100px',
        },
        {
            name: '',
            cell: row => (
                <ActionWrapper>
                    <IconButton noBg onClick={() => handleEditRole(row)}>
                        <MdEdit style={{ color: '#6b7280' }} />
                    </IconButton>
                    {!row.is_system_default && (
                        <IconButton noBg onClick={() => handleDeleteRole(row)}>
                            <MdDelete style={{ color: '#ef4444' }} />
                        </IconButton>
                    )}
                </ActionWrapper>
            ),
            ignoreRowClick: true,
            width: '100px',
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                fontWeight: '600',
                fontSize: '13px',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        cells: {
            style: {
                padding: '0.75rem 1rem',
            },
        },
        rows: {
            style: {
                '&:hover': {
                    backgroundColor: '#f9fafb',
                },
            },
        },
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <SectionTitle>{t('Users.Roles.title')}</SectionTitle>
                <MainButton onClick={handleAddRole}>
                    <MdAdd style={{ marginRight: '0.5rem' }} />
                    {t('Users.Roles.addRole')}
                </MainButton>
            </div>

            <StyledCard>
                {loading ? (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                        {t('common.loading')}
                    </div>
                ) : roles.length === 0 ? (
                    <EmptyState>
                        <MdLock />
                        <h3>{t('Users.Roles.noRoles')}</h3>
                        <p>{t('Users.Roles.noRolesDescription')}</p>
                    </EmptyState>
                ) : (
                    <DataTable
                        columns={columns}
                        data={roles}
                        pagination={false}
                        customStyles={customStyles}
                        highlightOnHover
                        responsive
                    />
                )}
            </StyledCard>

            {showModal && (
                <RoleModal
                    isOpen={showModal}
                    onClose={handleModalClose}
                    onSave={handleRoleSaved}
                    mode={modalMode}
                    role={selectedRole}
                    options={options}
                />
            )}
        </div>
    );
};

export default RolesManagement;
